import { createInstance, i18n, Resource, TFunction } from 'i18next';
import Backend from 'i18next-chained-backend';
import HttpApi from 'i18next-http-backend';
import LocalStorageBackend from 'i18next-localstorage-backend';
import resourcesToBackend from 'i18next-resources-to-backend';
import { NextRequest } from 'next/server';
import { Config } from 'next-i18n-router/dist/types';
import { initReactI18next } from 'react-i18next/initReactI18next';

import { Locale } from '../../../helpers/languages';
import { CF_COUNTRY_HEADER, LANGUAGE_PARAM } from '../../../helpers/variables';
import {
  AVAILABLE_LOCALES,
  DEFAULT_LOCALE,
  DEFAULT_NS,
  detectBrowserLanguage,
  detectUserLocale,
  NAMESPACES,
} from '@/modules/localization';

export const i18nConfig: Config = {
  locales: AVAILABLE_LOCALES,
  defaultLocale: DEFAULT_LOCALE,
  localeCookie: LANGUAGE_PARAM,
  localeDetector: (request: NextRequest) => {
    // Detect locale based on browser language and country code
    const countryCode = request.headers.get(CF_COUNTRY_HEADER) || '';
    const acceptLanguage = request.headers.get('Accept-Language') || '';
    const browserLanguage = detectBrowserLanguage(acceptLanguage)?.code;
    return detectUserLocale(browserLanguage || DEFAULT_LOCALE, countryCode);
  },
};

export async function initTranslations(
  locale: string,
  ns?: string | string[],
  i18nInstance?: i18n,
  resources?: Resource
) {
  i18nInstance = i18nInstance || createInstance();
  i18nInstance.use(initReactI18next);
  i18nInstance.use(Backend);

  await i18nInstance.init({
    load: 'currentOnly',
    lng: locale,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: AVAILABLE_LOCALES,
    defaultNS: DEFAULT_NS,
    fallbackNS: DEFAULT_NS,
    ns: ns ?? NAMESPACES,
    preload: typeof window === 'undefined' ? AVAILABLE_LOCALES : [],
    interpolation: { escapeValue: false },
    resources,
    detection: {
      order: ['cookie'],
      caches: ['cookie'],
    },
    partialBundledLanguages: true,
    backend: {
      backends: [
        LocalStorageBackend, // primary
        HttpApi, // 1st fallback
        resourcesToBackend(
          // 2nd fallback
          (language: string, namespace: string) =>
            import(`/locales/${language}/${namespace}.json`)
        ),
      ],
      backendOptions: [
        { projectId: 'levelup' },
        {
          loadPath: `https://api.i18nexus.com/project_resources/translations/{{lng}}/{{ns}}.json?api_key=${process.env.NEXT_PUBLIC_I18NEXUS_API_KEY}`,
        },
      ],
      cacheHitMode: 'refreshAndUpdateStore',
      refreshExpirationTime: Number(
        process.env.NEXT_PUBLIC_I18NEXUS_REFRESH_TIME || 60000
      ),
    },
  });

  return {
    i18n: i18nInstance,
    resources: { [locale]: i18nInstance.services.resourceStore.data[locale] },
  };
}

export const getServerTranslation = async (
  locale: string,
  ns: string | string[] = 'common'
) => {
  const { i18n: i18nextInstance } = await initTranslations(locale, ns);

  return {
    t: i18nextInstance.getFixedT(locale, Array.isArray(ns) ? ns[0] : ns),
    i18n: i18nextInstance,
  };
};

export const getLangAttr = (locale: string) => {
  if (!locale) return Locale.ENGLISH;
  if (locale === Locale.NORWEGIAN) return 'en-NO';

  return locale;
};

export type TranslationsState = {
  locale: Locale;
  lang: string;
};

export const translationsState: TranslationsState = {
  locale: DEFAULT_LOCALE,
  lang: getLangAttr(DEFAULT_LOCALE),
};

export const hasTranslation = (t: TFunction, key: string) => t(key) !== key;
